import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Label } from "./ui-bits";
import { MARKERS, classify } from "@/data/markers";
import { api } from "@/lib/auth";

// ---------- Result insights (plain-English change since previous test) ----------

const STATUS_WORD = { low: "below range", high: "above range", "in-range": "in range" };

function describe(m, prev, latest) {
  const diff = latest.value - prev.value;
  const pct = prev.value ? Math.round((Math.abs(diff) / prev.value) * 100) : null;
  const dir = Math.abs(diff) < 1e-9 ? "unchanged" : diff > 0 ? "rose" : "fell";
  const was = classify(m, prev.value), is = classify(m, latest.value);
  let sentence =
    dir === "unchanged"
      ? `${m.name} is unchanged at ${latest.value} ${m.unit} (${STATUS_WORD[is]}).`
      : `${m.name} ${dir} from ${prev.value} to ${latest.value} ${m.unit}${pct !== null ? ` (${diff > 0 ? "+" : "−"}${pct}%)` : ""} between ${prev.taken_at} and ${latest.taken_at}.`;
  let context = "";
  if (was !== is) context = ` It has moved from ${STATUS_WORD[was]} to ${STATUS_WORD[is]}.`;
  else if (is !== "in-range") context = ` It remains ${STATUS_WORD[is]}.`;
  else context = " It remains in range.";
  let clinical = "";
  if (m.id === "haematocrit") clinical = latest.value > 0.54 ? " Above 0.54 your clinician would normally act — expect a dose, interval or formulation review." : dir === "rose" ? " A rise is expected on treatment; what matters is that it stays below about 0.54." : "";
  if (m.id === "psa" && dir === "rose") clinical = diff > 1.4 ? " A rise of more than 1.4 ng/mL in a year meets the threshold for a urology discussion." : " A small rise in the early months of treatment is expected.";
  if (m.id === "total_testosterone") clinical = is === "in-range" && dir === "rose" ? " Treatment is aiming for the mid-normal range, not the top." : is === "low" ? " Still low — absorption, timing of the sample and dose will be reviewed." : is === "high" ? " Above range — doses are adjusted down rather than up here." : "";
  if ((m.id === "lh" || m.id === "fsh") && is === "low") clinical = " Suppression is expected while on testosterone and is not a problem in itself.";
  return { sentence, context, clinical, dir, status: is };
}

export function ResultInsights({ results }) {
  const insights = MARKERS.map((m) => {
    const items = results.filter((r) => r.marker === m.id);
    if (items.length < 2) return null;
    return { m, ...describe(m, items[items.length - 2], items[items.length - 1]) };
  }).filter(Boolean);

  const single = MARKERS.filter((m) => results.filter((r) => r.marker === m.id).length === 1).length;

  return (
    <div className="card-line rounded-2xl p-6 sm:p-8" data-testid="result-insights">
      <Label>What changed since your last test</Label>
      {insights.length === 0 ? (
        <p className="text-mute text-sm leading-relaxed mt-4" data-testid="insights-empty">
          {single > 0 ? `You have one result for ${single} marker${single > 1 ? "s" : ""}. Once a second result is added, this panel explains what moved and whether it matters.` : "Add two or more results for a marker and this panel explains what moved, in plain English."}
        </p>
      ) : (
        <ul className="mt-5 space-y-4">
          {insights.map((i) => {
            const Icon = i.dir === "rose" ? TrendingUp : i.dir === "fell" ? TrendingDown : Minus;
            const tone = i.status === "in-range" ? "text-teal" : i.status === "high" ? "text-rose" : "text-amber-300";
            return (
              <li key={i.m.id} className="flex gap-4" data-testid={`insight-${i.m.id}`}>
                <span className={`mt-0.5 w-8 h-8 rounded-full border border-line grid place-items-center shrink-0 ${tone}`}><Icon size={15} /></span>
                <p className="text-sm leading-relaxed text-bone/85">
                  {i.sentence}<span className="text-mute">{i.context}{i.clinical}</span>
                </p>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-xs text-dusk leading-relaxed mt-5">Automated summary for information only — it compares your two most recent values per marker against typical UK ranges. Your clinician interprets results in full context.</p>
    </div>
  );
}

// ---------- Monitoring schedule / plan ----------

export const PLAN_OPTIONS = [
  { id: "foundation", name: "Foundation", schedule: "3 months, then every 6 months" },
  { id: "precision", name: "Precision", schedule: "3 months, then every 3 months" },
  { id: "specialist", name: "Specialist", schedule: "6 weeks, then every 3 months" },
];

export function PlanForm({ path, current, onSaved, compact = false }) {
  const [plan, setPlan] = useState(current?.plan || "foundation");
  const [start, setStart] = useState(current?.plan_start || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setPlan(current?.plan || "foundation");
    setStart(current?.plan_start || "");
  }, [current?.plan, current?.plan_start]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      onSaved(await api(path, { method: "PATCH", body: { plan, plan_start: start } }));
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className={`grid ${compact ? "sm:grid-cols-[1fr,1fr,auto]" : "sm:grid-cols-2"} gap-3 items-end`} data-testid="plan-form">
      <label className="block">
        <span className="mono-label text-dusk block mb-2">Plan</span>
        <select className="field" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="plan-select">
          {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id} className="bg-surface">{p.name} — {p.schedule}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="mono-label text-dusk block mb-2">Treatment start date</span>
        <input type="date" required className="field" value={start} onChange={(e) => setStart(e.target.value)} data-testid="plan-start" />
      </label>
      <div className={compact ? "" : "sm:col-span-2"}>
        <button type="submit" disabled={busy} className="btn-ghost !py-2.5 !px-5 text-sm" data-testid="plan-save">{busy ? "Saving…" : "Save plan"}</button>
        {error && <p className="text-rose text-xs mt-2" data-testid="plan-error">{error}</p>}
      </div>
    </form>
  );
}

export function MonitoringCard({ monitoring, planPath, onSaved, editable = true }) {
  const m = monitoring;
  const days = m?.days_until;
  const tone = days == null ? "text-dusk" : days < 0 ? "text-rose" : days <= 14 ? "text-amber-300" : "text-teal";
  const headline = m?.next_due == null ? "No plan set" : days < 0 ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}` : days === 0 ? "Due today" : `Due in ${days} day${days === 1 ? "" : "s"}`;

  return (
    <div className="card-line rounded-2xl p-6 sm:p-8" data-testid="monitoring-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label><CalendarClock size={14} /> Next monitoring blood test</Label>
          <p className={`font-display text-4xl tracking-wide mt-4 ${tone}`} data-testid="monitoring-headline">{headline}</p>
          {m?.next_due && (
            <p className="text-sm text-mute mt-2" data-testid="monitoring-detail">
              {m.plan_name} plan · {m.schedule}. Next sample due <span className="text-bone/85 font-mono">{m.next_due}</span>, based on your {m.basis}. A reminder email is sent when it is within 7 days.
            </p>
          )}
          {!m?.next_due && <p className="text-sm text-mute mt-2">Set your plan and treatment start date to see when monitoring bloods are due and receive email reminders.</p>}
        </div>
        {m?.next_due && <Link to="/book" className="btn-primary !py-2.5 !px-5 text-sm whitespace-nowrap" data-testid="monitoring-book">Book blood test</Link>}
      </div>
      {editable && (
        <details className="mt-6 group" open={!m?.next_due}>
          <summary className="cursor-pointer text-sm text-bone/80 hover:text-teal list-none inline-flex items-center gap-2" data-testid="monitoring-edit-toggle">
            {m?.next_due ? "Change plan or start date" : "Set my plan"} <span className="text-teal">+</span>
          </summary>
          <div className="mt-4"><PlanForm path={planPath} current={m} onSaved={onSaved} compact /></div>
        </details>
      )}
    </div>
  );
}
